import type { ReactNode } from 'react'
import { Badge, Button, Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import {
  BadgeSlot,
  CardFeatureList,
  CardPrice,
  PLAN_CARD_HOVER,
  PLAN_CARD_SHELL,
  PlanDetailsAccordion,
} from './PlanCardParts'
import {
  CADENCE_LABEL,
  annualSavingCopy,
  annualSavingCents,
  billingLineCopy,
  ctaLabelFor,
  deltaChipCopy,
  isCatalogTwin,
  perMonthCents,
  planCardKind,
  rateNoteCopy,
  timingCopy,
} from './lib/helpers'
import type { CurrentPlan, PlanChangeTarget } from './lib/planChange.types'
import { PLAN_CHANGE_AUDIENCE_SUFFIXES } from './lib/tierDisplayNames'

export interface PlanOptionCardProps {
  target: PlanChangeTarget
  currentPlan: CurrentPlan
  /** The name running text uses, qualified for the other customer type's plans. */
  displayName: string
  title: string
  /** Who the plan is for, shown only when the view mixes customer types. */
  eyebrow: string | null
  includedFeatureIds: string[] | null
  isRecommended: boolean
  /** Whether any card in view is recommended. When none is, upgrade cards stay primary. */
  hasRecommendation: boolean
  onSelect: (target: PlanChangeTarget) => void
  disabled?: boolean
}

const KIND_EYEBROW = {
  upgrade: 'You add',
  lateral: 'What changes',
  downgrade: 'You give up',
} as const

const ctaLabelNode = (label: string): ReactNode => {
  const suffix = PLAN_CHANGE_AUDIENCE_SUFFIXES.find((candidate) =>
    label.endsWith(candidate)
  )
  if (!suffix) return label
  return (
    <span>
      {label.slice(0, -suffix.length)}{' '}
      <span className="block">{suffix.trim()}</span>
    </span>
  )
}

export const PlanOptionCard = ({
  target,
  currentPlan,
  displayName,
  title,
  eyebrow,
  includedFeatureIds,
  isRecommended,
  hasRecommendation,
  onSelect,
  disabled = false,
}: PlanOptionCardProps) => {
  const { promo } = target
  const kind = isCatalogTwin(target, currentPlan)
    ? 'upgrade'
    : planCardKind(target)
  const priceCents = promo
    ? promo.discountedAmountCents
    : target.effectiveAmountCents
  const topGains = target.marketedGains.slice(0, 3)
  const savingCents = annualSavingCents(currentPlan, target)
  const delta = deltaChipCopy(currentPlan, target)
  const rateNote = rateNoteCopy(currentPlan, target, displayName)

  return (
    <div
      data-testid={`plan-option-${target.lookupKey}`}
      className={mtc(
        PLAN_CARD_SHELL,
        PLAN_CARD_HOVER,
        '@container relative border-neutral-50 bg-neutral-0'
      )}
    >
      <BadgeSlot reserve={hasRecommendation} isRecommended={isRecommended} />
      <div className="flex min-h-8 items-center">
        <Text variant={TypographyStyles.DISPLAY_XS}>{title}</Text>
      </div>
      {eyebrow && (
        <Text
          testId="plan-audience"
          variant={TypographyStyles.EYEBROW}
          className="mt-2"
        >
          {eyebrow}
        </Text>
      )}

      <div className="mt-4 flex flex-col gap-1">
        <CardPrice
          effectiveAmountCents={perMonthCents(priceCents, target.cadence)}
          listAmountCents={perMonthCents(
            target.listAmountCents,
            target.cadence
          )}
          cadence="monthly"
          strikeListPrice={priceCents !== target.listAmountCents}
        />
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
          {billingLineCopy(target.cadence, priceCents)}
        </Text>
        {savingCents !== null && (
          <Text
            variant={TypographyStyles.BODY_SM}
            className="font-medium text-green-100"
          >
            {annualSavingCopy(savingCents)}
          </Text>
        )}
        {rateNote && (
          <Text variant={TypographyStyles.BODY_SM} className="text-green-100">
            {rateNote}
          </Text>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!isRecommended && kind === 'lateral' && target.lost.length === 0 && (
          <Badge size="sm" variant="secondary" color="olive">
            {`Same plan, ${CADENCE_LABEL[target.cadence].toLowerCase()} billing`}
          </Badge>
        )}
        {promo && (
          <Badge size="sm" variant="secondary" color="green">
            {promo.label}
          </Badge>
        )}
        <Badge size="sm" variant="secondary" color={delta.tone}>
          {delta.label}
        </Badge>
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
          {timingCopy(target, currentPlan)}
        </Text>
      </div>

      <div className="my-4 h-px bg-neutral-100" />

      <div className="flex flex-col gap-4">
        {kind === 'downgrade' ? (
          target.lost.length > 0 && (
            <CardFeatureList
              eyebrow={KIND_EYEBROW.downgrade}
              featureIds={target.lost}
              tone="loss"
            />
          )
        ) : (
          <>
            {topGains.length > 0 && (
              <CardFeatureList
                eyebrow={KIND_EYEBROW[kind]}
                featureIds={topGains}
                tone="gain"
              />
            )}
            {target.lost.length > 0 && (
              <CardFeatureList
                eyebrow={KIND_EYEBROW.downgrade}
                featureIds={target.lost}
                tone="loss"
              />
            )}
          </>
        )}
      </div>

      {includedFeatureIds !== null && (
        <div className="mt-2">
          <PlanDetailsAccordion featureIds={includedFeatureIds} tone="keep" />
        </div>
      )}

      <div className="mt-auto pt-[22px]">
        <Button
          className="h-auto min-h-10 w-full whitespace-normal py-2 text-center"
          variant={
            isRecommended || (!hasRecommendation && kind === 'upgrade')
              ? 'primary'
              : 'secondary'
          }
          disabled={disabled}
          onClick={() => onSelect(target)}
        >
          {ctaLabelNode(ctaLabelFor(target, currentPlan, displayName))}
        </Button>
      </div>
    </div>
  )
}
